/* eslint-disable @typescript-eslint/return-await */
import { catchError, firstValueFrom, from, map, mergeMap, of, pipe } from 'rxjs';
import { RECAPTCHA_ACTION } from '../../authentication/constant/recaptcha';
import Authorization from '../../authentication/models/authorization';
import {
  CreateCustomerParams,
  GetCustomerParams,
} from '../interfaces/customer';
import ErrorMapping from '../mappings/error.mapping';
import OracleProvider from '../providers/oracle-provider';
import SessionProvider from 'domains/core-base/authentication/providers/session-provider';
import OracleMapping from '../mappings/oracle-mapping';
import {
  CreateOpportunityParams,
  EnqueueOpportunity,
  PatchOpportunityParams,
} from '../interfaces/opportunity';

const oracleIsActivate = +import.meta.env.VITE_APP_ORACLE_CRM;

class OracleRepository {
  private auth: Authorization = Authorization.getInstance();

  constructor(
    private errorMapping: ErrorMapping,
    private sessionProvider: SessionProvider,
    private oracleMapping: OracleMapping,
  ) {}

  async getCustomer(params: GetCustomerParams): Promise<any> {
    if (!oracleIsActivate) return Promise.reject();

    const source$ = this.sessionProvider.getToken().pipe(
      map((sessionToken) => sessionToken),
      map((authorization) => ({
        authorization,
        documentId: params.documentId,
        documentType: params.documentType,
      })),
      mergeMap(OracleProvider.getCustomer),
      map((result) =>
        this.oracleMapping.formatGetCustomerResponse(result.response),
      ),
      map((response) => response.partyId),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async createCustomer(data: CreateCustomerParams): Promise<string> {
    if (!oracleIsActivate) return Promise.reject();

    const source$ = this.sessionProvider.getToken().pipe(
      map((sessionToken) => sessionToken),
      map((authorization) => ({
        authorization,
        customer: data,
      })),
      mergeMap(OracleProvider.createCustomer),
      map((result) =>
        this.oracleMapping.formatGetCustomerResponse(result.response),
      ),
      map((response) => response.partyId),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async enqueueOpportunity(params: EnqueueOpportunity): Promise<string> {
    if (!oracleIsActivate) return Promise.reject(new Error('Oracle CRM is not activated'));
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        authorization: this.auth.token,
        data: params,
        recaptcha: recaptchaToken,
      })),
      mergeMap(OracleProvider.createEnqueueOportunity),
      map(({ response }) => response.identificadorAsesor),
      pipe(catchError(()=>of('')))
    );
    return firstValueFrom(source$);
  }

  async opportunityCreation(
    data: CreateOpportunityParams,
  ): Promise<string> {
    if (!oracleIsActivate) return Promise.reject();

    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        data,
        recaptcha: recaptchaToken,
        authorization: this.auth.token,
      })),
      mergeMap(OracleProvider.createOpportunnity),
      map((result) =>
        this.oracleMapping.formatGetOpportunityResponse(result.response),
      ),
      map((response) => response.optyNumber),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async patchPhaseOpportunity(
    data: PatchOpportunityParams,
    options: { optyNumber: string },
  ): Promise<string> {
    if (!oracleIsActivate) return Promise.reject();

    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        data,
        optyNumber: options.optyNumber,
        recaptcha: recaptchaToken,
        authorization: this.auth.token,
      })),
      mergeMap(OracleProvider.patchPhase),
      map((result) =>
        this.oracleMapping.formatGetOpportunityResponse(result.response),
      ),
      map((response) => response.optyNumber),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async patchEnqueueOpportunity(
    data: PatchOpportunityParams,
    options: { optyNumber: string },
  ): Promise<string> {
    if (!oracleIsActivate) return Promise.reject(new Error('Oracle CRM is not activated'));

    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        data,
        advisorIdentifier: options.optyNumber,
        authorization: this.auth.token,
        recaptcha: recaptchaToken,
      })),
      mergeMap(OracleProvider.updateEnqueueOportunity),
      map(({response}) => response.identificadorAsesor),
      pipe(catchError(()=>of('')))
    );

    return firstValueFrom(source$);
  }

}

export const oracleRepository = new OracleRepository(
  new ErrorMapping(),
  new SessionProvider(),
  new OracleMapping(),
);
